import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Pencil, Trash2, Upload, LogOut, ShieldCheck, Loader2, X, ArrowLeft, EyeOff, Eye, Save, Users,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WORK_PROFILES = ['Excellent', 'Very Good', 'Good', 'Average'];

const emptyEmployee = () => ({
  name: '',
  designation: '',
  date_of_joining: '',
  work_profile: 'Excellent',
  qualification: '',
  contact: '',
  address: '',
  specialization: '',
  assignments: [],
  photo_path: null,
  sort_order: 100,
  published: true,
});

const formatError = (detail) => {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const AdminEmployeesPage = () => {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // employee object being edited (or new)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [assignInput, setAssignInput] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileRef = useRef(null);

  // Redirect if not authed after bootstrap
  useEffect(() => {
    if (ready && !user) navigate('/admin/login');
  }, [ready, user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/employees`);
      setEmployees(data);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ready && user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user]);

  const openNew = () => {
    setEditing(emptyEmployee());
    setAssignInput('');
    setError('');
  };

  const openEdit = (emp) => {
    setEditing({ ...emp, assignments: emp.assignments || [] });
    setAssignInput('');
    setError('');
  };

  const cancel = () => {
    setEditing(null);
    setError('');
  };

  const handleField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const addAssignment = () => {
    const v = assignInput.trim();
    if (!v) return;
    setEditing((e) => ({ ...e, assignments: [...(e.assignments || []), v] }));
    setAssignInput('');
  };

  const removeAssignment = (i) =>
    setEditing((e) => ({
      ...e,
      assignments: e.assignments.filter((_, idx) => idx !== i),
    }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/admin/employees/photo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditing((e) => ({ ...e, photo_path: data.path }));
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Photo upload failed');
    } finally {
      setPhotoUploading(false);
    }
  };

  const save = async () => {
    if (!editing.name || !editing.designation) {
      setError('Name and Designation are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: editing.name,
        designation: editing.designation,
        date_of_joining: editing.date_of_joining,
        work_profile: editing.work_profile || null,
        qualification: editing.qualification,
        contact: editing.contact,
        address: editing.address,
        specialization: editing.specialization || null,
        assignments: editing.assignments || [],
        photo_path: editing.photo_path || null,
        sort_order: Number(editing.sort_order) || 100,
        published: !!editing.published,
      };
      if (editing.id) {
        await axios.put(`${API}/admin/employees/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/admin/employees`, payload);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (emp) => {
    if (!window.confirm(`Delete ${emp.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/employees/${emp.id}`);
      await load();
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    }
  };

  const photoUrl = (p) => (p ? `${API}/media/${p}` : null);

  if (!ready) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-employees-page">
      {/* Top bar */}
      <header className="bg-[#0a0f1e] text-white sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/team')}
              className="text-white/60 hover:text-white transition-colors flex items-center gap-1 text-xs mr-2"
              title="Back to Team Directory"
            >
              <ArrowLeft size={14} />
              Dashboard
            </button>
            <div className="w-9 h-9 bg-white/10 flex items-center justify-center">
              <ShieldCheck className="text-brand-yellow" size={18} />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-brand-yellow uppercase">
                Admin Console
              </div>
              <div className="font-heading text-lg font-bold">Flat List</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/team')}
              className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Open the grouped Team Directory view"
              data-testid="admin-view-directory-btn"
            >
              <Users size={14} />
              Team Directory
            </button>
            <span className="text-xs text-white/60 hidden md:block">{user?.email}</span>
            <Button
              onClick={async () => {
                await logout();
                navigate('/admin/login');
              }}
              className="bg-white/10 hover:bg-white/20 text-white rounded-none border border-white/10 h-9 px-3 text-sm"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} className="mr-1.5" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">Employees</h1>
            <p className="text-sm text-gray-500">
              {employees.length} record{employees.length === 1 ? '' : 's'}. Public listing sorted by
              order, then name.
            </p>
          </div>
          <Button
            onClick={openNew}
            className="bg-brand-blue hover:bg-brand-blue-hover text-white rounded-none h-11 px-5"
            data-testid="admin-add-employee-btn"
          >
            <Plus size={16} className="mr-2" /> Add Employee
          </Button>
        </div>

        {error && !editing && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">
            {error}
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : employees.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-gray-300 bg-white">
            <p className="text-gray-500 mb-4">No employees yet.</p>
            <Button onClick={openNew} className="rounded-none">
              <Plus size={16} className="mr-2" /> Add your first employee
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 divide-y divide-gray-100" data-testid="admin-employees-list">
            {employees.map((emp) => (
              <div key={emp.id} className="p-5 flex items-center gap-5 hover:bg-gray-50 transition-colors">
                <div className="flex-shrink-0 w-28 h-28 bg-gray-100 border border-gray-200 overflow-hidden">
                  {emp.photo_path ? (
                    <img
                      src={photoUrl(emp.photo_path)}
                      alt={emp.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-gray-400">
                      {emp.name?.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-base font-bold text-gray-900 truncate">
                      {emp.name}
                    </h3>
                    <span className="text-[10px] font-mono tracking-wider px-1.5 py-0.5 bg-brand-blue/10 text-brand-blue uppercase">
                      {emp.designation}
                    </span>
                    {!emp.published && (
                      <span className="text-[10px] font-mono tracking-wider px-1.5 py-0.5 bg-gray-200 text-gray-600 uppercase">
                        Draft
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    DoJ {emp.date_of_joining} · {emp.qualification} · {emp.contact}
                  </p>
                  {emp.specialization && (
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                      Specialization: {emp.specialization}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEdit(emp)}
                    className="p-2 text-gray-500 hover:text-brand-blue hover:bg-brand-blue/10 transition-colors"
                    title="Edit"
                    data-testid={`edit-${emp.id}`}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => remove(emp)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                    title="Delete"
                    data-testid={`delete-${emp.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm flex items-stretch justify-end">
          <div
            className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl"
            data-testid="admin-employee-editor"
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] text-gray-500 uppercase">
                  {editing.id ? 'Editing' : 'New'} Employee
                </div>
                <h2 className="font-heading text-xl font-bold text-gray-900">
                  {editing.name || 'Untitled'}
                </h2>
              </div>
              <button
                onClick={cancel}
                className="p-2 text-gray-500 hover:text-gray-900"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Photo */}
              <div>
                <label className="block text-xs font-mono tracking-widest text-gray-600 uppercase mb-2">
                  Photo
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 border border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden">
                    {editing.photo_path ? (
                      <img
                        src={photoUrl(editing.photo_path)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">No photo</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadPhoto(e.target.files?.[0])}
                    />
                    <Button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={photoUploading}
                      className="rounded-none bg-gray-900 hover:bg-black text-white"
                      data-testid="admin-upload-photo-btn"
                    >
                      {photoUploading ? (
                        <>
                          <Loader2 size={14} className="mr-2 animate-spin" /> Uploading…
                        </>
                      ) : (
                        <>
                          <Upload size={14} className="mr-2" /> Upload / Replace
                        </>
                      )}
                    </Button>
                    <p className="text-[11px] text-gray-500 mt-1">JPEG / PNG · up to 8 MB.</p>
                  </div>
                </div>
              </div>

              <FieldGroup label="Name*">
                <input
                  value={editing.name}
                  onChange={(e) => handleField('name', e.target.value)}
                  className="admin-input"
                  data-testid="editor-name"
                />
              </FieldGroup>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Designation*">
                  <input
                    value={editing.designation}
                    onChange={(e) => handleField('designation', e.target.value)}
                    className="admin-input"
                    data-testid="editor-designation"
                  />
                </FieldGroup>
                <FieldGroup label="Date of Joining">
                  <input
                    value={editing.date_of_joining}
                    onChange={(e) => handleField('date_of_joining', e.target.value)}
                    placeholder="19-07-2011"
                    className="admin-input"
                  />
                </FieldGroup>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Work Profile">
                  <select
                    value={editing.work_profile || ''}
                    onChange={(e) => handleField('work_profile', e.target.value)}
                    className="admin-input"
                  >
                    <option value="">— select —</option>
                    {WORK_PROFILES.map((w) => (
                      <option key={w} value={w}>
                        {w}
                      </option>
                    ))}
                  </select>
                </FieldGroup>
                <FieldGroup label="Specialization">
                  <input
                    value={editing.specialization || ''}
                    onChange={(e) => handleField('specialization', e.target.value)}
                    className="admin-input"
                    placeholder="Audit & Compliances"
                  />
                </FieldGroup>
              </div>

              <FieldGroup label="Qualification">
                <input
                  value={editing.qualification}
                  onChange={(e) => handleField('qualification', e.target.value)}
                  placeholder="FCA, CS, DISA"
                  className="admin-input"
                />
              </FieldGroup>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Contact">
                  <input
                    value={editing.contact}
                    onChange={(e) => handleField('contact', e.target.value)}
                    placeholder="+91 99646 66007"
                    className="admin-input"
                  />
                </FieldGroup>
                <FieldGroup label="Sort Order">
                  <input
                    type="number"
                    value={editing.sort_order}
                    onChange={(e) => handleField('sort_order', e.target.value)}
                    className="admin-input"
                  />
                </FieldGroup>
              </div>

              <FieldGroup label="Address">
                <textarea
                  value={editing.address}
                  onChange={(e) => handleField('address', e.target.value)}
                  rows={2}
                  className="admin-input"
                />
              </FieldGroup>

              <FieldGroup label="Assignments Handled">
                <div className="flex gap-2 mb-2">
                  <input
                    value={assignInput}
                    onChange={(e) => setAssignInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addAssignment();
                      }
                    }}
                    placeholder="e.g. Statutory Audit"
                    className="admin-input flex-1"
                  />
                  <Button
                    type="button"
                    onClick={addAssignment}
                    className="rounded-none bg-gray-900 hover:bg-black text-white px-4"
                  >
                    Add
                  </Button>
                </div>
                {(editing.assignments || []).length > 0 && (
                  <ul className="space-y-1">
                    {editing.assignments.map((a, i) => (
                      <li
                        key={i}
                        className="flex items-center justify-between text-sm bg-gray-50 border border-gray-200 px-3 py-1.5"
                      >
                        <span>{a}</span>
                        <button
                          onClick={() => removeAssignment(i)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </FieldGroup>

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => handleField('published', !editing.published)}
                  className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-medium border ${
                    editing.published
                      ? 'bg-green-50 border-green-200 text-green-700'
                      : 'bg-gray-50 border-gray-200 text-gray-600'
                  }`}
                  type="button"
                >
                  {editing.published ? <Eye size={14} /> : <EyeOff size={14} />}
                  {editing.published ? 'Published' : 'Draft'}
                </button>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">
                  {error}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <Button
                onClick={cancel}
                className="rounded-none bg-white hover:bg-gray-100 text-gray-700 border border-gray-300"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="rounded-none bg-brand-blue hover:bg-brand-blue-hover text-white"
                data-testid="admin-save-employee-btn"
              >
                {saving ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Save size={14} className="mr-2" /> Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-input {
          width: 100%;
          border: 1px solid #d1d5db;
          padding: 10px 12px;
          font-size: 14px;
          outline: none;
          background: white;
        }
        .admin-input:focus { border-color: #111827; }
      `}</style>
    </div>
  );
};

const FieldGroup = ({ label, children }) => (
  <div>
    <label className="block text-xs font-mono tracking-widest text-gray-600 uppercase mb-2">
      {label}
    </label>
    {children}
  </div>
);

export default AdminEmployeesPage;
